import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  HelpCircle, CheckCircle2, Loader2, MessageSquare, AlertTriangle,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { fmtDate } from "../lib/status";
import { VIEW_OPTIONS, tagByCode, SEVERITY_DOT_CLASS } from "../lib/damages";

const ACTION_OPTIONS = [
  { value: "discussed",                 label: "Discussed with customer (no record change)" },
  { value: "damage_record_updated",     label: "Damage record was updated" },
  { value: "confirmed_with_customer",   label: "Confirmed with customer (item exists as recorded)" },
];

/**
 * Customer-flagged-damage clarification card.
 *
 * Only renders when there is something to show: either an unresolved
 * clarification, or a recently-resolved one (so staff can read the closing
 * note). Wording is deliberately neutral; we never attribute blame.
 */
export default function DamageClarificationCard({ bookingId, onResolved }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [action, setAction] = useState("discussed");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/bookings/${bookingId}/damage-clarification`);
      setData(data);
    } catch (e) {
      // 404s on legacy bookings: silently swallow.
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return null;
  // Nothing to show if no flagged damages ever existed.
  const hasAnything = data && (
    data.needs_clarification ||
    data.resolved_at ||
    (data.flagged_damage_ids || []).length > 0
  );
  if (!hasAnything) return null;

  const unresolved = !!data.needs_clarification;
  const palette = unresolved
    ? { border: "border-rose-200",   bg: "bg-rose-50",   text: "text-rose-900",   pill: "bg-rose-100 border-rose-200 text-rose-900",   bar: "bg-rose-500" }
    : { border: "border-emerald-200", bg: "bg-emerald-50", text: "text-emerald-900", pill: "bg-emerald-100 border-emerald-200 text-emerald-900", bar: "bg-emerald-500" };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/bookings/${bookingId}/damage-clarification/resolve`, {
        business_reply: reply.trim() || null,
        action,
      });
      toast.success("Clarification marked as resolved. The customer has been notified.");
      await load();
      onResolved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="damage-clarification-card"
      className={`rounded-xl border ${palette.border} ${palette.bg} overflow-hidden`}
    >
      <div className={`h-1 ${palette.bar}`} />
      <div className="p-5 sm:p-6 space-y-4">
        <div className="flex items-start gap-3">
          <div className={`w-9 h-9 rounded-lg grid place-items-center border ${palette.pill} flex-shrink-0`}>
            {unresolved ? <AlertTriangle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className={`text-[10px] uppercase tracking-[0.2em] font-bold ${palette.text}`}>
              {unresolved ? "Action needed · Customer clarification" : "Resolved · Customer clarification"}
            </div>
            <h3 className={`font-display text-lg font-semibold ${palette.text}`}>
              {unresolved
                ? "Customer requested clarification on existing damage"
                : "Clarification was reviewed and resolved"}
            </h3>
            <p className="text-sm text-slate-700 mt-1">
              {data.customer_name || "Customer"} flagged {(data.flagged_damage_ids || []).length}
              {" "}item{(data.flagged_damage_ids || []).length === 1 ? "" : "s"} when reviewing the pre-pickup damage sheet.
              {data.requested_at && <> · Requested {fmtDate(data.requested_at) || data.requested_at.slice(0, 10)}</>}
            </p>
          </div>
        </div>

        {data.customer_message && (
          <blockquote
            className="border-l-4 border-amber-300 bg-amber-50 px-3 py-2 rounded-r-md text-sm text-amber-900"
            data-testid="damage-clarification-customer-message"
          >
            <div className="text-[10px] uppercase tracking-wider font-semibold mb-0.5 inline-flex items-center gap-1">
              <MessageSquare className="w-3 h-3" /> Customer message
            </div>
            {data.customer_message}
          </blockquote>
        )}

        {(data.flagged_damages || []).length > 0 && (
          <div className="space-y-1.5" data-testid="damage-clarification-flagged-list">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">
              Flagged items ({data.flagged_damages.length})
            </div>
            <ul className="divide-y divide-slate-100 rounded-md border border-slate-200 bg-white">
              {data.flagged_damages.map((d) => {
                const tag = tagByCode(d.tag_code);
                return (
                  <li key={d.id} className="px-3 py-2 text-sm flex items-center gap-2 flex-wrap" data-testid={`damage-clarification-flagged-${d.id}`}>
                    <HelpCircle className="w-3.5 h-3.5 text-amber-700" />
                    <span className="font-medium">
                      {d.tag_code === "other" ? (d.tag_label || tag.label) : tag.label}
                    </span>
                    <span className={`inline-block w-2 h-2 rounded-full ${SEVERITY_DOT_CLASS[d.severity] || "bg-slate-400"}`} />
                    <span className="text-xs uppercase tracking-wider text-slate-500">{d.severity}</span>
                    <span className="text-xs text-slate-500">· {VIEW_OPTIONS.find((v) => v.value === d.view)?.label || d.view}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {unresolved ? (
          <div className="space-y-3" data-testid="damage-clarification-resolve-form">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-700">
                Resolution action
              </span>
              <select
                value={action}
                onChange={(e) => setAction(e.target.value)}
                data-testid="damage-clarification-action-select"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm"
              >
                {ACTION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-700">
                Reply / internal note (optional)
              </span>
              <textarea
                value={reply}
                onChange={(e) => setReply(e.target.value.slice(0, 1500))}
                rows={3}
                maxLength={1500}
                placeholder="Short, neutral wording: e.g. 'Reviewed with customer at pickup, agreed on existing damage list.'"
                data-testid="damage-clarification-reply"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm"
              />
            </label>
            <button
              onClick={submit}
              disabled={busy}
              data-testid="damage-clarification-resolve-btn"
              className="w-full sm:w-auto px-4 py-2 rounded-lg btn-brand text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              {busy ? "Resolving…" : "Mark as resolved"}
            </button>
          </div>
        ) : (
          <div className="text-sm text-emerald-900 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 space-y-0.5" data-testid="damage-clarification-resolved-box">
            <div>
              Resolved by <b>{data.resolved_by}</b> on {fmtDate(data.resolved_at) || data.resolved_at?.slice(0, 10)}
              {data.resolution_action && <> · action: <code className="text-xs">{data.resolution_action.replace(/_/g, " ")}</code></>}
            </div>
            {data.business_reply && (
              <div className="text-xs text-slate-700 italic">&ldquo;{data.business_reply}&rdquo;</div>
            )}
            <div className="text-xs text-emerald-800 mt-1">
              A neutral confirmation has been sent to the customer.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
